import { Badge, Box, Flex, FlexProps, Image, Text, VStack } from '@chakra-ui/react';
import { InternationalEventResponse, MatchState } from 'api/responseTypes';
import { FormattedMessage } from 'react-intl';

const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', 'Noto Sans', sans-serif";
const TEXT_PRIMARY = '#1B1917';
const CITY_COLOR = '#C03224';

interface InternationalEventListItemProps {
  event: InternationalEventResponse;
  onCardClick: () => void;
  /** Constrain the row width (e.g. for horizontal card rows); defaults to full width. */
  width?: FlexProps['width'];
}

/**
 * Compact horizontal row for an international event: thumbnail on the trailing
 * (right, RTL) side, with title / date / location / starting price stacked beside it.
 * Used in the home section and the listing/country pages.
 */
export const InternationalEventListItem = ({
  event,
  onCardClick,
  width,
}: InternationalEventListItemProps) => {
  const { title, imageLink, startingPrice, currency, city, country, state, date, time } = event;

  const isSoldOut = state === MatchState.SOLD_OUT;
  const location = [city, country].filter(Boolean).join(' - ');

  return (
    <Flex
      as='li'
      listStyleType='none'
      dir='rtl'
      align='center'
      gap={3}
      width={width}
      flexShrink={width ? 0 : undefined}
      bg='transparent'
      cursor={isSoldOut ? 'not-allowed' : 'pointer'}
      pointerEvents={isSoldOut ? 'none' : 'unset'}
      transition='opacity 0.2s ease'
      _hover={{ opacity: isSoldOut ? 1 : 0.85 }}
      onClick={onCardClick}
      tabIndex={isSoldOut ? -1 : 0}
      role='button'
      aria-disabled={isSoldOut}
      aria-label={`${title}${date ? ` - ${date}` : ''}${city ? ` - ${city}` : ''}`}
      onKeyDown={(e: React.KeyboardEvent) => {
        if ((e.key === 'Enter' || e.key === ' ') && !isSoldOut) {
          e.preventDefault();
          onCardClick();
        }
      }}
    >
      {/* Thumbnail — trailing (right) side in RTL */}
      <Box flexShrink={0} boxSize='118px' borderRadius='13.25px' overflow='hidden' bg='dark.D100'>
        <Image
          src={imageLink ?? undefined}
          alt={title}
          width='100%'
          height='100%'
          objectFit='cover'
          filter={isSoldOut ? 'grayscale(100%) opacity(0.7)' : 'none'}
        />
      </Box>

      {/* Text column — sits beside the 118px image */}
      <VStack flex='1' align='stretch' justify='center' spacing='2px' px={1} minH='118px' minW={0}>
        <Text
          fontFamily={FONT_FAMILY}
          fontWeight={700}
          fontSize='16px'
          lineHeight='1.4'
          letterSpacing='-0.01em'
          textAlign='right'
          color={TEXT_PRIMARY}
        >
          {title}
        </Text>

        {date && (
          <Text
            fontFamily={FONT_FAMILY}
            fontWeight={400}
            fontSize='14px'
            lineHeight='1.6'
            textAlign='right'
            color={isSoldOut ? 'dark.D200' : TEXT_PRIMARY}
          >
            {date} {time}
          </Text>
        )}

        {location && (
          <Text
            fontFamily={FONT_FAMILY}
            fontWeight={400}
            fontSize='14px'
            lineHeight='1.6'
            textAlign='right'
            color={isSoldOut ? 'dark.D500' : CITY_COLOR}
            noOfLines={1}
          >
            {location}
          </Text>
        )}

        {isSoldOut ? (
          <Badge
            alignSelf='start'
            bg='red'
            color='white'
            px={2}
            py={1}
            borderRadius='full'
            textStyle='bodySmallSemibold'
          >
            <FormattedMessage defaultMessage='Sold Out' id='concerts.soldOut' />
          </Badge>
        ) : (
          <Text
            fontFamily={FONT_FAMILY}
            fontWeight={400}
            fontSize='14px'
            lineHeight='1.6'
            textAlign='right'
            color={TEXT_PRIMARY}
          >
            <FormattedMessage defaultMessage='تبدأ من' id='internationalEvents.startingFrom' />
            {` ${new Intl.NumberFormat().format(startingPrice)} ${currency}`}
          </Text>
        )}
      </VStack>
    </Flex>
  );
};
